import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import { Autocomplete, Box, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, createFilterOptions } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import useInventory, { ContentInventory, InventoryVersion } from '../../hooks/useInventory';
import { stripHtml } from '../../utils/common';
import { readInventoryTemplateSortAlphabetically, writeInventoryTemplateSortAlphabetically } from './inventoryTemplateSortPreference';

export interface InventoryTemplateValue {
    inventoryId: number;
    inventorySubId: number;
}

interface InventoryTemplateAutocompleteProps {
    setSelectedInventoryVersion: React.Dispatch<React.SetStateAction<InventoryVersion | undefined>>;
    setSelectedInventory?: React.Dispatch<React.SetStateAction<ContentInventory | undefined>>;
    value?: InventoryTemplateValue | null;
    onClear?: () => void;
}

type AutoCompleteOption = {
    inventoryName: string;
    inventoryId: number;
    versionId: number;
    versionName: string;
    tags: string[];
    text: string;
};

const InventoryTemplateAutocomplete = ({
    setSelectedInventoryVersion,
    setSelectedInventory,
    value,
    onClear,
}: InventoryTemplateAutocompleteProps) => {
    const { data: contentInventory, isLoading: isInventoryLoading } = useInventory();
    const [inventories, setInventories] = useState<ContentInventory[]>([]);
    const [sortAlphabetically, setSortAlphabetically] = useState(readInventoryTemplateSortAlphabetically);

    useEffect(() => {
        if (contentInventory) {
            setInventories(contentInventory);
        }
    }, [contentInventory]);

    const options = useMemo((): AutoCompleteOption[] => {
        const list: AutoCompleteOption[] = [];
        for (let i = 0; i < inventories.length; i += 1) {
            for (let j = 0; j < inventories[i].versions.length; j += 1) {
                const version = inventories[i].versions[j];
                list.push({
                    inventoryName: inventories[i].name,
                    inventoryId: inventories[i].inventoryId,
                    versionId: version.inventorySubId,
                    versionName: version.versionName,
                    tags: (version.tags ?? []).map(tag => tag.tagText).filter(Boolean),
                    text: version.text ? stripHtml(version.text) : '',
                });
            }
        }
        if (sortAlphabetically) {
            list.sort(
                (a, b) =>
                    (a.inventoryName ?? '').localeCompare(b.inventoryName ?? '', undefined, { sensitivity: 'base' }) ||
                    (a.versionName ?? '').localeCompare(b.versionName ?? '', undefined, { sensitivity: 'base' }),
            );
        }
        return list;
    }, [inventories, sortAlphabetically]);

    const autocompleteValue = useMemo((): AutoCompleteOption | null => {
        if (!value || value.inventoryId <= 0 || value.inventorySubId <= 0) {
            return null;
        }
        return options.find(option => option.inventoryId === value.inventoryId && option.versionId === value.inventorySubId) ?? null;
    }, [options, value]);

    const toggleSortAlphabetically = () => {
        setSortAlphabetically(prev => {
            const next = !prev;
            writeInventoryTemplateSortAlphabetically(next);
            return next;
        });
    };

    return (
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Autocomplete<AutoCompleteOption>
                disableClearable={false}
                clearOnEscape
                fullWidth
                sx={{ flex: 1 }}
                loading={isInventoryLoading}
                loadingText="Loading templates..."
                options={options}
                value={autocompleteValue}
                groupBy={option => option.inventoryName}
                getOptionLabel={option => `${option.inventoryName} - ${option.versionName}`}
                renderOption={(props, option) => (
                    <li {...props} key={`${option.inventoryId}-${option.versionId}`}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Box>
                                {option.inventoryName} - {option.versionName}
                            </Box>
                            {option.tags.length > 0 && (
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                    {option.tags.slice(0, 6).map(tag => (
                                        <Chip key={tag} size="small" label={tag} variant="outlined" />
                                    ))}
                                    {option.tags.length > 6 && (
                                        <Chip size="small" label={`+${option.tags.length - 6}`} variant="outlined" />
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </li>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Search Templates"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isInventoryLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{ mt: 0, mb: 0 }}
                    />
                )}
                onChange={(_, option) => {
                    if (!option) {
                        setSelectedInventoryVersion(undefined);
                        setSelectedInventory?.(undefined);
                        onClear?.();
                        return;
                    }

                    const inventory = inventories.find(item => item.inventoryId === option.inventoryId);
                    const version = inventory?.versions.find(item => item.inventorySubId === option.versionId);

                    if (inventory && version) {
                        setSelectedInventory?.(inventory);
                    }

                    setSelectedInventoryVersion(version);
                }}
                filterOptions={createFilterOptions({
                    matchFrom: 'any',
                    stringify: option => `${option.inventoryName} ${option.versionName} ${option.tags.join(' ')} ${option.text}`,
                    ignoreAccents: true,
                    ignoreCase: true,
                    limit: 500,
                })}
                isOptionEqualToValue={(option, selected) =>
                    option.inventoryId === selected.inventoryId && option.versionId === selected.versionId
                }
            />
            <Tooltip title="Sort alphabetically or by creation order">
                <IconButton
                    size="medium"
                    onClick={toggleSortAlphabetically}
                    aria-label="toggle alphabetical sort"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, flexShrink: 0, height: 39 }}
                >
                    <SortByAlphaIcon fontSize="small" color={sortAlphabetically ? 'primary' : 'secondary'} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
};

export default InventoryTemplateAutocomplete;
